/**
 * P3 데이터 품질 페이지 (RUNBOOK P4).
 *
 * **구현 순서에서 이 페이지가 가장 먼저인 이유**: 다른 페이지의 모든 숫자가
 * 얼마나 믿을 만한지를 여기서 설명한다. 실측이 STALE 44% · limitYn 55% 였으므로
 * (§15.3) 이 페이지 없이 가동률을 보여주는 것은 오해를 만드는 일이다.
 *
 * 구성은 **질문의 순서**를 따른다 — 위젯을 나열하지 않는다.
 */

import { useQuery } from '@tanstack/react-query'

import { KpiStrip } from '@/components/KpiStrip'
import { Section } from '@/components/Section'
import { fetchPlain } from '@/lib/api'
import type { ManifestData } from '@/lib/types'
import { CollectionCalendar } from './quality/CollectionCalendar'
import { NoteCategories } from './quality/NoteCategories'
import { OperatorMatrix } from './quality/OperatorMatrix'
import { PopulationWaterfall } from './quality/PopulationWaterfall'
import { StaleHistogram } from './quality/StaleHistogram'
import { StatMixTimeline } from './quality/StatMixTimeline'
import './QualityPage.css'

export function QualityPage() {
  const { data: manifest } = useQuery({
    queryKey: ['meta', 'manifest'],
    queryFn: ({ signal }) => fetchPlain<ManifestData>('/meta/manifest', signal),
    staleTime: 60 * 1000,
  })

  return (
    <div className="quality">
      {/* 페이지 제목은 sub-nav 에 있다 — h1 을 두 번 두지 않는다 */}
      <header className="quality__intro">
        <p className="quality__lead">
          이 페이지는 <strong>다른 모든 숫자의 신뢰 근거</strong>입니다. 관측되지 않은 시간은
          0 이 아니라 결측으로 표시하고, 비교할 수 없는 대상은 숨기지 않고 사유와 함께
          분리합니다.
        </p>
        {manifest?.runtime_provisional && (
          <p className="quality__warn" role="alert">
            게이트 판정값이 <strong>잠정</strong>입니다 — probe 실측이 완료되지 않았습니다.
            이 상태의 수치를 공표하지 마세요.
          </p>
        )}
        {manifest && manifest.warnings.length > 0 && (
          <details className="quality__warnbox">
            <summary>
              빌드 경고 <span className="num">{manifest.warnings.length}</span>건
            </summary>
            <ul>
              {manifest.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </details>
        )}
      </header>

      <KpiStrip />

      <Section
        index={1}
        title="이 숫자는 누구의 것인가"
        lead="52만기 전량에서 무엇을 왜 뺐는지, 그리고 절반이 넘는 '이용 제한'이 대체 무엇인지."
      >
        <PopulationWaterfall />
        <NoteCategories />
      </Section>

      <Section
        index={2}
        title="얼마나 믿을 수 있나"
        lead="상태 코드가 시간을 어떻게 나눠 가졌는지, 그리고 사업자 연계가 끊긴 채 방치된 시간이 얼마인지."
      >
        <StatMixTimeline />
        <StaleHistogram />
      </Section>

      <Section
        index={3}
        title="누구를 비교할 수 있나"
        lead="화이트리스트를 통과한 사업자만 순위·비교에 쓴다. 통과하지 못한 곳도 사유와 함께 남긴다."
        layout="full"
      >
        <OperatorMatrix />
      </Section>

      <Section
        index={4}
        title="우리가 놓친 것은 무엇인가"
        lead="변경 피드는 소급 조회가 불가능하다 — 못 본 시간은 영원히 모른다."
        layout="full"
      >
        <CollectionCalendar />
      </Section>
    </div>
  )
}
